import React, {useEffect, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {Button, Checkbox, Dropdown, Form, Icon, Input, Label, Message, Select} from "semantic-ui-react";
import {yupResolver} from "@hookform/resolvers/yup";
import {useAppDispatch, useAppSelector} from "../../hooks/hooks";
import {
    createDartsTournament, editDartsTournament,
    hideTournamentForm
} from "./TournamentsCRUD.slice";
import {
    ICreateTournamentPayload,
    IDartsTour,
    IEditTournamentPayload
} from "./TournamentsCRUD.types";
import {checkIsExistTournament} from "./helpers/checkIsExistTournament";
import useTournamentsOptions from "./helpers/useTournamentsOptions";
import * as yup from "yup";
import styles from "./TournamentsCRUD.form.module.scss";
import {toastr} from "react-redux-toastr";
import {formatSelectOptions} from "../../helpers/formatSelectOptions";
import useTourTitle from "./hooks/useTourTitle";

interface ITournamentsFormProps {
    tournamentsIds: IDartsTour[],
}

const TournamentsCRUDForm: React.FC<ITournamentsFormProps> = (
    {
        tournamentsIds,
    }) => {
    const dispatch = useAppDispatch();
    const {
        editMode,
        editTournament,
        competitionOptions,
        groupOptions,
        seriesOptions,
        dartsTournaments,
        players
    } = useAppSelector(state => state.TournamentsCRUD);
    const [selectedSeriesId, setSelectedSeriesId] = useState<any>(null);
    const [tourType, setTourType] = useState<any>(null);
    const [generatedTitle, setGeneratedTitle] = useState<{ series: string, week: string }>({
        series: "",
        week: "",
    });
    const [tourPlayers, setTourPlayers] = useState<any>([]);
    const [isActive, setIsActive] = useState(true);
    const {series, competitions, tourTypes} = useTournamentsOptions(competitionOptions, groupOptions, seriesOptions)
    const {title, handleTitleChange, setTitle} = useTourTitle(generatedTitle)

    useEffect(() => {
        setDefaultFormValues()
    }, [editMode, editTournament]);


    const setDefaultFormValues = () => {
        if (editMode && editTournament) {
            const editTour = dartsTournaments.find(
                (tour: { id: number; }) => tour.id === editTournament.id
            );
            const defaultCompetitionsOptions = competitionOptions.find(
                (competition) => competition.id === editTour!.competitionId
            );
            const defaultSeriesOption = seriesOptions?.find(
                (option) => option.id === editTour!.seriesId
            );
            if (defaultSeriesOption) {
                setValue("series", defaultSeriesOption.id);
            }
            if (defaultCompetitionsOptions) {
                setValue("competition", defaultCompetitionsOptions.id);
            }
            setValue("week", editTour!.week);
            if (defaultSeriesOption) {
                setSelectedSeriesId(defaultSeriesOption!.id);
            }
            setTourPlayers(editTournament.playerIDs)
            setTourType(editTournament.type)
            setTitle(editTour!.title)
        }
    }

    let validationSchema = yup.object().shape({
        competition: yup.number().required(),
        tourType: yup.number().required(),
    });

    const { control, formState: { errors }, handleSubmit, setValue, getValues} = useForm({
        mode: "onSubmit",
        resolver: yupResolver(validationSchema),
    });


    const submitNewTournament = () => {
        const newTournament: ICreateTournamentPayload = {
            competitionId: getValues("competition"),
            week: getValues("week"),
            seriesId: selectedSeriesId,
            type: tourType,
            playerIDs: tourPlayers,
            isActive,
            title
        }
        const isTourExisting = checkIsExistTournament({
            tournamentsIds,
            editMode,
            editTournament,
            selectedWeek: newTournament.week,
            selectedCompetition: newTournament.competitionId
        });
        if (isTourExisting) {
            return toastr.error("", "Tournament is already exists")
        }
        dispatch(createDartsTournament(newTournament));
    };

    const updateTournament = () => {
        const updatedTournament: IEditTournamentPayload = {
            id: editTournament!.id,
            competitionId: getValues("competition"),
            week: getValues("week"),
            seriesId: selectedSeriesId,
            type: tourType.value,
            playerIDs: tourPlayers,
            title,
            isActive: true
        };
        const isTourExisting = checkIsExistTournament({
            tournamentsIds,
            editMode,
            editTournament,
            selectedWeek: updatedTournament.week,
            selectedCompetition: updatedTournament.competitionId
        });
        if (isTourExisting) {
            return toastr.error("", "Tournament is already exists")
        }
        dispatch(editDartsTournament(updatedTournament));
    };

    return (
        <div className="form-overlay-general">
            <Form
                className={styles.toursForm}
                onSubmit={
                    editMode
                        ? handleSubmit(updateTournament)
                        : handleSubmit(submitNewTournament)
                }
            >
                <Icon
                    color="red"
                    name="close"
                    size="large"
                    onClick={() => dispatch(hideTournamentForm())}
                    style={{float: "right", cursor: "pointer"}}
                />

                <Form.Field>
                    <Label>Competition:</Label>
                    <Controller
                        name="competition"
                        control={control}
                        render={(props) => (
                            <Select
                                value={getValues("competition")}
                                id="competitionName"
                                options={competitions}
                                placeholder="Select competition"
                                onChange={(e, {value}) => props.onChange(value)}
                            />
                        )}
                    />
                    {errors.competition && (
                        <Message negative>{errors.competition.message}</Message>
                    )}
                </Form.Field>

                <Form.Field>
                    <Label>Series:</Label>
                    <Controller
                        name="series"
                        control={control}
                        render={(props) => (
                            <Select
                                value={selectedSeriesId}
                                id="series"
                                options={series}
                                placeholder="Select series"
                                onChange={(e, {value}) => {
                                    props.onChange(value)
                                    setSelectedSeriesId(value)
                                    const option = series.find(series => series.value === value)
                                    if (option) {
                                        setGeneratedTitle({...generatedTitle, series: option.text})
                                    }
                                }}
                            />
                        )}
                    />
                    {errors.series && (
                        <Message negative>{errors.series.message}</Message>
                    )}
                </Form.Field>

                <Form.Field>
                    <Label>Week:</Label>
                    <Controller
                        name="week"
                        control={control}
                        render={(props) => (
                            <Input
                                value={getValues("week")}
                                id="week"
                                placeholder="Select week"
                                onChange={(e, {value}) => {
                                    props.onChange(value)
                                    if (value) {
                                        setGeneratedTitle({...generatedTitle, week: value})
                                    }
                                }}
                            />
                        )}
                    />
                    {errors.week && <Message negative>{errors.week.message}</Message>}
                </Form.Field>

                <Form.Field>
                    <Label>Title:</Label>
                    <Input
                        readOnly={editTournament}
                        placeholder={"Title"}
                        value={title}
                        onChange={(e, {value}) => {
                            handleTitleChange(value)
                        }}

                    />
                </Form.Field>

                <Form.Field>
                    <Label>Tournament's type:</Label>
                    <Controller
                        name="tourType"
                        control={control}
                        render={(props) => (
                            <Select
                                value={tourType}
                                id="tourType"
                                options={tourTypes}
                                placeholder="Select type"
                                onChange={(e, {value}) => {
                                    props.onChange(value)
                                    setTourType(value)
                                }}
                            />
                        )}
                    />
                    {errors.tourType && (
                        <Message negative>{errors.tourType.message}</Message>
                    )}
                </Form.Field>

                <Form.Field>
                    <Label>Tournament's players:</Label>
                    <Dropdown
                        clearable={true}
                        placeholder='Players'
                        fluid
                        multiple
                        selection
                        value={tourPlayers}
                        onChange={(e, {value}) => setTourPlayers(value)}
                        options={formatSelectOptions(players)}
                        search
                    />
                </Form.Field>

                <Form.Field>
                    <Checkbox
                        label={"Active"}
                        checked={isActive}
                        onClick={() => setIsActive(!isActive)}
                    />
                </Form.Field>
                <Button color="green" id="save_tournament" type="submit">
                    Submit
                </Button>
            </Form>
        </div>
    );
};

export default TournamentsCRUDForm;
